import React from "react";
import { Button } from "semantic-ui-react";
import { WidgetProps } from "../types";
import Prompt from "../Prompt";
import PillPicker from "../PillPicker";
import Summary, { SummaryField, SummaryPills, Actions } from "../Summary";
import { QUADRANTS } from "./QuadrantsDeconstruction";

/**
 * "Putting it all together" — exactly three words per quadrant, from the
 * ingredients chosen in the previous challenge.
 */
const WORDS_PER_QUADRANT = 3;

// As with the deconstruction, the stored description carries the instruction
// and the four explanations together. Split them so neither is said twice.
const PROMPT =
  "Choose three words from your ingredients list for each of the four quadrants — no more, no less.";

const GUIDANCE: Record<string, string> = {
  emotional:
    "About the heart: how you want to feel, and what you want others to feel when they’re with you.",
  physical:
    "What is tangible in your life — your health, where you live, your family, your commute.",
  intellectual:
    "What occupies and feeds your brain: your work, studies, interests, or ideas that fascinate you.",
  spiritual:
    "What reflects your beliefs and character — the non-physical part of your identity, your passions or personal philosophy.",
};

const QuadrantsReconstruction: React.FC<WidgetProps> = ({
  payload,
  update,
  submit,
  submitting,
  finished,
  question,
  currentUser,
}) => {
  const setQuadrant = (key: string) => (next: string[]) => {
    // Exactly three, no more — the constraint is the exercise.
    if (next.length > WORDS_PER_QUADRANT) return;
    update({ ...payload, [key]: next });
  };

  const complete = QUADRANTS.every(
    (q) => (payload[q.key] || []).length === WORDS_PER_QUADRANT,
  );

  if (finished) {
    return (
      <Summary question={question} currentUser={currentUser} prompt={PROMPT}>
        {QUADRANTS.map((quadrant) => (
          <SummaryField key={quadrant.key} label={quadrant.label}>
            <SummaryPills values={payload[quadrant.key] || []} />
          </SummaryField>
        ))}
      </Summary>
    );
  }

  return (
    <div className="ui form assignment-form">
      <Prompt question={question} currentUser={currentUser} fallback={PROMPT} />

      {QUADRANTS.map((quadrant) => {
        const chosen = (payload[quadrant.key] || []).length;
        return (
          <div key={quadrant.key} className="ix-section">
            <div className="ix-label">
              {quadrant.label} — {chosen} of {WORDS_PER_QUADRANT}
            </div>
            <p className="ix-hint">{GUIDANCE[quadrant.key]}</p>
            <PillPicker
              selected={payload[quadrant.key] || []}
              options={payload[`${quadrant.key}_options`] || []}
              onChange={setQuadrant(quadrant.key)}
              placeholder="Add your own"
            />
          </div>
        );
      })}

      <p className="dtlyl-ayse-message">
        Before you finish, look at your choices and ask yourself: do they make me
        happy? Do they capture my imagination? If the answer is yes, great. If
        not, redo it until it does.
      </p>

      <Actions>
        <Button
          primary
          circular
          className="challenge-submit"
          disabled={!complete || submitting}
          loading={submitting}
          onClick={submit}
        >
          Submit
        </Button>
      </Actions>
    </div>
  );
};

export default QuadrantsReconstruction;
